import { supabase } from '../lib/supabase';

// ─── Helpers ─────────────────────────────────────────────────────────────────

const toCamel = (row) => {
  if (!row) return null;
  return {
    id: row?.id,
    userId: row?.user_id,
    fullName: row?.full_name,
    email: row?.email,
    phone: row?.phone,
    whatsapp: row?.whatsapp,
    passwordSet: row?.password_set,
    idDocumentUrl: row?.id_document_url,
    idDocumentType: row?.id_document_type,
    professionalLicenseUrl: row?.professional_license_url,
    licenseNumber: row?.license_number,
    yearsExperience: row?.years_experience,
    bio: row?.bio,
    city: row?.city,
    commune: row?.commune,
    address: row?.address,
    serviceZones: row?.service_zones || [],
    languages: row?.languages || ['Français'],
    websiteUrl: row?.website_url,
    linkedinUrl: row?.linkedin_url,
    hourlyRate: row?.hourly_rate,
    dailyRate: row?.daily_rate,
    currency: row?.currency,
    availableDays: row?.available_days || [],
    availableHoursStart: row?.available_hours_start,
    availableHoursEnd: row?.available_hours_end,
    avatarUrl: row?.avatar_url,
    category: row?.category,
    specialty: row?.specialty,
    tags: row?.tags || [],
    certifications: row?.certifications || [],
    applicationStatus: row?.application_status,
    rejectionReason: row?.rejection_reason,
    commissionRate: row?.commission_rate != null ? parseFloat(row?.commission_rate) : null,
    submittedAt: row?.submitted_at,
    createdAt: row?.created_at,
    updatedAt: row?.updated_at,
  };
};

// ─── Application CRUD ─────────────────────────────────────────────────────────

export const createApplication = async (data) => {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    const userId = (!authError && user?.id) ? user?.id : null;
    const payload = {
      user_id: userId,
      full_name: data?.fullName || '',
      email: data?.email || '',
      phone: data?.phone || null,
      whatsapp: data?.whatsapp || null,
      category: data?.category || '',
      application_status: 'draft',
    };
    const { data: row, error } = await supabase
      ?.from('professional_applications')
      ?.insert(payload)
      ?.select()
      ?.single();
    if (error) throw error;
    return { data: toCamel(row), error: null };
  } catch (err) {
    console.error('createApplication error:', err?.message);
    return { data: null, error: err?.message };
  }
};

export const updateApplication = async (id, data) => {
  try {
    const payload = {};
    if (data?.fullName !== undefined) payload.full_name = data?.fullName;
    if (data?.email !== undefined) payload.email = data?.email;
    if (data?.phone !== undefined) payload.phone = data?.phone;
    if (data?.whatsapp !== undefined) payload.whatsapp = data?.whatsapp;
    if (data?.idDocumentUrl !== undefined) payload.id_document_url = data?.idDocumentUrl;
    if (data?.idDocumentType !== undefined) payload.id_document_type = data?.idDocumentType;
    if (data?.professionalLicenseUrl !== undefined) payload.professional_license_url = data?.professionalLicenseUrl;
    if (data?.licenseNumber !== undefined) payload.license_number = data?.licenseNumber;
    if (data?.yearsExperience !== undefined) payload.years_experience = parseInt(data?.yearsExperience) || 0;
    if (data?.bio !== undefined) payload.bio = data?.bio;
    if (data?.city !== undefined) payload.city = data?.city;
    if (data?.commune !== undefined) payload.commune = data?.commune;
    if (data?.address !== undefined) payload.address = data?.address;
    if (data?.serviceZones !== undefined) payload.service_zones = data?.serviceZones;
    if (data?.languages !== undefined) payload.languages = data?.languages;
    if (data?.websiteUrl !== undefined) payload.website_url = data?.websiteUrl;
    if (data?.linkedinUrl !== undefined) payload.linkedin_url = data?.linkedinUrl;
    if (data?.hourlyRate !== undefined) payload.hourly_rate = data?.hourlyRate ? parseFloat(data?.hourlyRate) : null;
    if (data?.dailyRate !== undefined) payload.daily_rate = data?.dailyRate ? parseFloat(data?.dailyRate) : null;
    if (data?.currency !== undefined) payload.currency = data?.currency;
    if (data?.availableDays !== undefined) payload.available_days = data?.availableDays;
    if (data?.availableHoursStart !== undefined) payload.available_hours_start = data?.availableHoursStart;
    if (data?.availableHoursEnd !== undefined) payload.available_hours_end = data?.availableHoursEnd;
    if (data?.avatarUrl !== undefined) payload.avatar_url = data?.avatarUrl;
    if (data?.category !== undefined) payload.category = data?.category;
    if (data?.specialty !== undefined) payload.specialty = data?.specialty;
    if (data?.tags !== undefined) payload.tags = data?.tags;
    if (data?.certifications !== undefined) payload.certifications = data?.certifications;
    if (data?.applicationStatus !== undefined) payload.application_status = data?.applicationStatus;
    if (data?.commissionRate !== undefined) payload.commission_rate = data?.commissionRate !== null ? parseFloat(data?.commissionRate) : null;
    if (data?.userId !== undefined) payload.user_id = data?.userId;

    const { data: row, error } = await supabase
      ?.from('professional_applications')
      ?.update(payload)
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return { data: toCamel(row), error: null };
  } catch (err) {
    console.error('updateApplication error:', err?.message);
    return { data: null, error: err?.message };
  }
};

export const submitApplication = async (id) => {
  try {
    const { data: row, error } = await supabase
      ?.from('professional_applications')
      ?.update({ application_status: 'submitted', submitted_at: new Date()?.toISOString() })
      ?.eq('id', id)
      ?.select()
      ?.single();
    if (error) throw error;
    return { data: toCamel(row), error: null };
  } catch (err) {
    console.error('submitApplication error:', err?.message);
    return { data: null, error: err?.message };
  }
};

// ─── Email Verification ───────────────────────────────────────────────────────

/**
 * Generate a verification token, store it on the application, and send the confirmation email.
 * Token expires in 24 hours.
 */
export const sendEmailVerification = async (applicationId, fullName, email) => {
  try {
    // Generate a random 48-char hex token
    const tokenBytes = new Uint8Array(24);
    crypto.getRandomValues(tokenBytes);
    const token = Array.from(tokenBytes)?.map(b => b?.toString(16)?.padStart(2, '0'))?.join('');
    const expiresAt = new Date(Date.now() + 24 * 60 * 60 * 1000)?.toISOString();

    // Store token on the application row
    const { error: updateErr } = await supabase
      ?.from('professional_applications')
      ?.update({
        email_verification_token: token,
        email_verification_expires_at: expiresAt,
        email_verified: false,
      })
      ?.eq('id', applicationId);

    if (updateErr) throw updateErr;

    // Build the verification URL
    const appUrl = import.meta.env?.VITE_APP_URL || 'https://kapitalstates.com';
    const verificationUrl = `${appUrl}/professional-email-verification?token=${token}`;

    // Send the email via the transactional edge function
    const TRANSACTIONAL_URL = `${import.meta.env?.VITE_SUPABASE_URL}/functions/v1/send-transactional-emails`;
    const { data: sessionData } = await supabase?.auth?.getSession();
    const accessToken = sessionData?.session?.access_token;
    const headers = { 'Content-Type': 'application/json' };
    if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;

    const response = await fetch(TRANSACTIONAL_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        type: 'professional_email_verification',
        data: { full_name: fullName, email, verification_url: verificationUrl },
      }),
    });

    if (!response?.ok) {
      const err = await response?.json()?.catch(() => ({}));
      console.warn('[sendEmailVerification] Email send failed:', err?.error);
      return { success: false, error: err?.error || 'Email send failed' };
    }

    return { success: true, error: null };
  } catch (err) {
    console.error('sendEmailVerification error:', err?.message);
    return { success: false, error: err?.message };
  }
};

/**
 * Check if the application's email has been verified.
 */
export const checkEmailVerified = async (applicationId) => {
  try {
    const { data, error } = await supabase
      ?.from('professional_applications')
      ?.select('email_verified')
      ?.eq('id', applicationId)
      ?.single();
    if (error) throw error;
    return { verified: data?.email_verified === true, error: null };
  } catch (err) {
    console.error('checkEmailVerified error:', err?.message);
    return { verified: false, error: err?.message };
  }
};

export const getMyApplications = async () => {
  try {
    const { data: { user } } = await supabase?.auth?.getUser();
    if (!user) return { data: [], error: null };
    const { data, error } = await supabase
      ?.from('professional_applications')
      ?.select('*')
      ?.eq('user_id', user?.id)
      ?.order('created_at', { ascending: false });
    if (error) throw error;
    return { data: (data || [])?.map(toCamel), error: null };
  } catch (err) {
    console.error('getMyApplications error:', err?.message);
    return { data: [], error: err?.message };
  }
};

// ─── File Upload ──────────────────────────────────────────────────────────────

export const uploadCertification = async (file, applicationId) => {
  try {
    const ext = file?.name?.split('.')?.pop();
    const path = `${applicationId}/${Date.now()}_${Math.random()?.toString(36)?.slice(2)}.${ext}`;
    const { error: uploadError } = await supabase?.storage
      ?.from('professional-certifications')
      ?.upload(path, file, { upsert: false });
    if (uploadError) throw uploadError;

    const { data: signedData, error: signError } = await supabase?.storage
      ?.from('professional-certifications')
      ?.createSignedUrl(path, 60 * 60 * 24 * 365);
    if (signError) throw signError;

    return { url: signedData?.signedUrl, path, error: null };
  } catch (err) {
    console.error('uploadCertification error:', err?.message);
    return { url: null, path: null, error: err?.message };
  }
};

export const uploadIdDocument = async (file, applicationId) => {
  try {
    const ext = file?.name?.split('.')?.pop();
    const path = `${applicationId}/id_${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase?.storage
      ?.from('professional-certifications')
      ?.upload(path, file, { upsert: true });
    if (uploadError) throw uploadError;

    const { data: signedData, error: signError } = await supabase?.storage
      ?.from('professional-certifications')
      ?.createSignedUrl(path, 60 * 60 * 24 * 365);
    if (signError) throw signError;

    return { url: signedData?.signedUrl, path, error: null };
  } catch (err) {
    console.error('uploadIdDocument error:', err?.message);
    return { url: null, path: null, error: err?.message };
  }
};
